import { Link } from 'react-router-dom'

import { RoomSetup } from '@/components/room/RoomSetup'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/lib/auth'
import { useGameAttention } from '@/lib/game-attention'
import { useI18n } from '@/lib/i18n'
import { languageNameKey } from '@/lib/lobby'
import { returnTargetFrom, signInUrlReturningTo } from '@/lib/return-target'
import { statusI18nSegment } from '@/lib/room-status-label'
import { getStartedGameRoute } from '@/lib/started-game-route'
import { cn, formatDate } from '@/lib/utils'

export function LobbyMyGames({ awaitingOnly }: { awaitingOnly: boolean }) {
  const { user, authResolved } = useAuth()
  const { t } = useI18n()
  const attention = useGameAttention()

  if (!authResolved)
    return <p className="text-sm text-muted-foreground">{t('forum.loading')}</p>

  if (!user) {
    return (
      <Button asChild data-testid="my-games-signin">
        <Link to={signInUrlReturningTo(returnTargetFrom(window.location))}>
          {t('nav.signIn')}
        </Link>
      </Button>
    )
  }

  if (!attention.data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {attention.isError ? t('forum.errorGeneric') : t('forum.loading')}
        </p>
        {attention.isError && (
          <Button
            variant="outline"
            onClick={() => {
              void attention.refetch()
            }}
          >
            {t('social.retry')}
          </Button>
        )}
      </div>
    )
  }

  const items = attention.data.items
    .filter(item => !awaitingOnly || item.isAwaitingInput)
    .sort((a, b) => Number(b.isAwaitingInput) - Number(a.isAwaitingInput)
      || b.createdAt.getTime() - a.createdAt.getTime())

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="my-games-empty">
        {t(awaitingOnly ? 'dashboard.games.emptyFilter' : 'dashboard.games.empty')}
      </p>
    )
  }

  return (
    <ul className="space-y-3" data-testid="my-games-list">
      {items.map(item => (
        <li
          key={item.id}
          data-testid="my-game-row"
          data-game-id={item.gameId ?? undefined}
          data-room-id={item.roomId ?? undefined}
        >
          <Card data-testid={`my-game-${item.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex flex-wrap items-start justify-between gap-2 text-base">
                <span className="min-w-0 space-y-0.5">
                  {item.title && <span className="block break-words">{item.title}</span>}
                  <RoomSetup
                    compact
                    gameSetting={item.gameSetting}
                    seatCount={item.numberOfPlayers}
                  />
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  <Badge
                    variant={item.isAwaitingInput ? 'default' : 'secondary'}
                    data-testid={item.isAwaitingInput ? `my-game-awaiting-${item.id}` : 'my-game-status'}
                    className="whitespace-normal"
                  >
                    {item.isAwaitingInput
                      ? t('gameAttention.awaitingInput')
                      : t(`dashboard.games.statuses.${statusI18nSegment(item.status)}`)}
                  </Badge>
                  <Badge variant="secondary">
                    {t(languageNameKey(item.language ?? 'en'))}
                  </Badge>
                </span>
              </CardTitle>
              <p className="text-xs text-muted-foreground">{formatDate(item.createdAt)}</p>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className={cn('rounded px-1.5 py-0.5', item.isOwned
                  ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                  : 'bg-violet-500/10 text-violet-600 dark:text-violet-400')}
                >
                  {t(item.isOwned ? 'auth.dashboard.gameOwnedBadge' : 'auth.dashboard.gameJoinedBadge')}
                </span>
                <span>{t(item.isMultiplayer ? 'dashboard.games.playModes.multiplayer' : 'dashboard.games.playModes.solo')}</span>
              </div>
              <Button size="sm" variant="outline" asChild>
                <Link
                  data-testid="my-game-return"
                  to={item.roomCode
                    ? `/room/${item.roomCode}`
                    : getStartedGameRoute(item.gameId ?? item.id, item.gameSetting ?? '')}
                >
                  {t(item.roomCode ? 'lobby.returnToRoom' : 'dashboard.games.actions.play')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  )
}
